import EventEmitter from 'eventemitter3';
import GameObject from '../GameObject/GameObject';
import { GameObjectEvent } from '../GameObject/GameObjectEvent';
import { IGameState } from '../State/Game/IGameState';
import System from '../System/System';
import { Random } from '../Utility/Random';

export interface ISceneParams {
    [key: string]: any;
}

export enum SceneEvent {
    GAME_OBJECT_ADDED = 'gameObjectAdded',
    GAME_OBJECT_REMOVED = 'gameObjectRemoved',
}

export default class Scene extends EventEmitter {
    public readonly id: string;
    public index: number;
    public sceneManagerID: string;
    public gameObjects: GameObject[];
    public systems: System[];
    public params: ISceneParams;
    public loaded: boolean;
    public active: boolean;

    constructor() {
        super();
        this.id = `${this.constructor.name}_${Random.ID()}`;
        this.gameObjects = [];
        this.systems = [];
        this.loaded = false;
        this.active = false;

        this.addGameObject = this.addGameObject.bind(this);
        this.removeGameObject = this.removeGameObject.bind(this);
    }

    /**
     * Called every time the scene is started before preload (if not already loaded).
     */
    public init(): void {}

    /**
     * Called when scene is started. Load any required assets for the scene.
     */
    public async preload() {
        // Preload systems in order they are added.
        for (let system of this.systems) {
            await system.preload();
        }
    }

    /**
     * Remove all gameobjects and then remove all systems. Systems removed last so gameobjects know which systems
     * to be removed from as well.
     */
    public unload() {
        // Filter and destroy root game object.
        // They'll destroy their children and so on.
        const rootGameObjects: GameObject[] = this.gameObjects.filter(
            go => go.parent === undefined || go.parent === null
        );
        for (let i = rootGameObjects.length - 1; i >= 0; i--) {
            rootGameObjects[i].destroy();
        }

        this.systems.splice(0).forEach(system => {
            system.destroy();
            this.removeSystem(system);
        });

        this.removeAllListeners();
    }

    public start() {
        // Start all systems.
        this.systems.forEach(system => system.start());

        // After all systems have been started prepare the current scene.
        this.systems.forEach(system => {
            system.handleStateChanged(system.state.getState());
        });
    }

    public stop() {
        for (let i = 0, n = this.systems.length; i < n; i++) {
            this.systems[i].stop();
        }
    }

    public update(): void {
        for (let i = 0, n = this.systems.length; i < n; i++) {
            if (this.systems[i].isActive()) {
                this.systems[i].update();
            }
        }

        for (let i = 0, n = this.systems.length; i < n; i++) {
            if (this.systems[i].isActive()) {
                this.systems[i].lateUpdate();
            }
        }
    }

    public addGameObject(gameObject: GameObject): void {
        if (this.hasGameObject(gameObject)) return;

        gameObject.scene = this;
        this.gameObjects.push(gameObject);

        gameObject.on(GameObjectEvent.CHILD_ADDED, this.addGameObject);

        gameObject.on(GameObjectEvent.CHILD_REMOVED, this.removeGameObject);

        gameObject.on(GameObjectEvent.DESTROYED, this.removeGameObject);

        gameObject.children.forEach(this.addGameObject);

        this.emit(SceneEvent.GAME_OBJECT_ADDED, gameObject);
    }

    public hasGameObject(gameObject: GameObject): boolean {
        return this.gameObjects.indexOf(gameObject) !== -1;
    }

    public removeGameObject(gameObject: GameObject): void {
        if (!this.hasGameObject(gameObject)) return;

        this.gameObjects.splice(this.gameObjects.indexOf(gameObject), 1);

        gameObject.off(GameObjectEvent.CHILD_ADDED, this.addGameObject);
        gameObject.off(GameObjectEvent.CHILD_REMOVED, this.removeGameObject);
        gameObject.off(GameObjectEvent.DESTROYED, this.removeGameObject);

        gameObject.children.forEach(this.removeGameObject);

        gameObject.scene = null;
        this.emit(SceneEvent.GAME_OBJECT_REMOVED, gameObject);
    }

    public addSystem(system: System): void {
        if (this.hasSystem(system)) return;

        this.systems.push(system);
        system.scene = this;
    }

    public hasSystem(system: System): boolean {
        return this.systems.indexOf(system) !== -1;
    }

    public removeSystem(system: System): void {
        if (!this.hasSystem(system)) return;

        this.systems.splice(this.systems.indexOf(system), 1);
        system.scene = null;
    }

    public toString(): string {
        return this.id;
    }

    public handleStateChange(state: IGameState) {
        for (let i = 0, n = this.systems.length; i < n; i++) {
            this.systems[i].handleStateChanged(state);
        }
    }
}
